import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Camera, Loader2, X, CheckCircle, AlertCircle } from 'lucide-react';
import ProductExtractionResults from '../../components/survey/ProductExtractionResults';
import PhotoValidationModal from '../../components/survey/PhotoValidationModal';

const API_URL = import.meta.env.VITE_API_URL || '';

interface ExtractedProduct {
  name: string;
  brand?: string | null;
  category?: string | null;
  confidence: number;
  quantity?: number | null;
}

interface UploadedImage {
  s3_key: string;
  url: string;
  extracted_products: ExtractedProduct[];
  extraction_status: 'pending' | 'processing' | 'completed' | 'failed' | 'uploading';
  image_quality?: string | null;
  upload_timestamp: string;
  localPreview?: string;
  rejection_reason?: string;
}

interface SessionData {
  survey_id: number;
  session_id: number;
  question_id: string;
  images: UploadedImage[];
}

const MobilePhotoUpload: React.FC = () => {
  const { uploadToken } = useParams<{ uploadToken: string }>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const objectUrlsRef = useRef<string[]>([]);

  const [session, setSession] = useState<SessionData | null>(null);
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpired, setIsExpired] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [uploadingKeys, setUploadingKeys] = useState<Set<string>>(new Set());
  const [validationModal, setValidationModal] = useState<{
    isOpen: boolean;
    error: string;
    s3Key: string;
  }>({ isOpen: false, error: '', s3Key: '' });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Abort any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Revoke all object URLs
      objectUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  // Auto-clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (uploadToken) {
      validateSession();
    }
  }, [uploadToken]);

  const validateSession = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/photo-upload/session/${uploadToken}/status`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error('Session not found');
      }

      const data = await response.json();

      if (data.expired) {
        setIsExpired(true);
        setError('This upload session has expired. Please scan the QR code again.');
      } else {
        setSession({
          survey_id: 0,
          session_id: data.session_id,
          question_id: data.question_id,
          images: data.images || []
        });
        setImages(data.images || []);
      }
    } catch (err) {
      setError('Invalid or expired upload session');
      setIsExpired(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const fileList = event.target.files;

      if (!fileList || fileList.length === 0) {
        return;
      }

      // IMPORTANT: Copy file BEFORE resetting input, as reset clears the FileList
      const file = fileList[0];

      // Reset file input to allow re-selecting the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      if (!session || !uploadToken) {
        setError('Session not ready. Please wait and try again.');
        return;
      }

      if (file.size > 15 * 1024 * 1024) {
        setError('File size exceeds 15MB limit');
        return;
      }

      setError(null);

      // Create preview URL
      const localPreview = URL.createObjectURL(file);
      objectUrlsRef.current.push(localPreview);

      // Generate temporary key
      const tempKey = `uploading-${Date.now()}`;

      // Create placeholder image
      const placeholderImage: UploadedImage = {
        s3_key: tempKey,
        url: '',
        extracted_products: [],
        extraction_status: 'uploading',
        upload_timestamp: new Date().toISOString(),
        localPreview
      };

      // Update state immediately to show preview
      setImages(prevImages => [...prevImages, placeholderImage]);
      setUploadingKeys(prev => new Set(prev).add(tempKey));

      // Upload in background
      const doUpload = async () => {
        try {
          // Convert to base64
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(file);
          });

          const response = await fetch(`${API_URL}/api/photo-upload/upload`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              upload_token: uploadToken,
              image_base64: base64
            }),
            credentials: 'include'
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || 'Upload failed');
          }

          const result = await response.json();

          const uploadedImage: UploadedImage = {
            ...result.image,
            localPreview
          };

          // Replace placeholder with real image
          setImages(prevImages => prevImages.map(img =>
            img.s3_key === tempKey ? uploadedImage : img
          ));

          // Check for validation failure
          if (uploadedImage.extraction_status === 'failed' && !result.image?.show_text_fallback) {
            setValidationModal({
              isOpen: true,
              error: result.image?.rejection_reason || 'Image could not be processed.',
              s3Key: uploadedImage.s3_key
            });
          }
        } catch (err) {
          // Remove placeholder on error
          URL.revokeObjectURL(localPreview);
          objectUrlsRef.current = objectUrlsRef.current.filter(u => u !== localPreview);
          setImages(prevImages => prevImages.filter(img => img.s3_key !== tempKey));
          setError(err instanceof Error ? err.message : 'Upload failed');
        } finally {
          setUploadingKeys(prev => {
            const next = new Set(prev);
            next.delete(tempKey);
            return next;
          });
        }
      };

      doUpload();
    } catch (err) {
      setError('Failed to process photo. Please try again.');
    }
  };

  const handleRemoveImage = async (s3Key: string) => {
    const imageToRemove = images.find(img => img.s3_key === s3Key);
    if (imageToRemove?.localPreview) {
      URL.revokeObjectURL(imageToRemove.localPreview);
      objectUrlsRef.current = objectUrlsRef.current.filter(u => u !== imageToRemove.localPreview);
    }
    setImages(prev => prev.filter(img => img.s3_key !== s3Key));

    // Also remove from Redis session so desktop doesn't see the removed image
    if (uploadToken) {
      try {
        await fetch(
          `${API_URL}/api/photo-upload/session/${uploadToken}/image/${encodeURIComponent(s3Key)}`,
          { method: 'DELETE', credentials: 'include' }
        );
      } catch (err) {
        console.error('Failed to remove image from session:', err);
      }
    }
  };

  const handleRetake = async () => {
    const s3Key = validationModal.s3Key;
    setValidationModal({ isOpen: false, error: '', s3Key: '' });
    await handleRemoveImage(s3Key);
    // Trigger camera immediately after modal closes
    setTimeout(() => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }, 100);
  };

  const handleContinueAnyway = () => {
    setValidationModal({ isOpen: false, error: '', s3Key: '' });
  };

  const handleDone = async () => {
    if (!uploadToken || images.length === 0) {
      setIsDone(true);
      return;
    }

    setIsSyncing(true);

    try {
      const response = await fetch(
        `${API_URL}/api/photo-upload/session/${uploadToken}/complete`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to sync with desktop');
      }

      const result = await response.json();

      if (result.success) {
        setIsDone(true);
      } else {
        throw new Error('Sync failed');
      }
    } catch (err) {
      setError('Failed to sync photos. Your photos are saved - please check your computer.');
      // Still allow completing after error
      setTimeout(() => setIsDone(true), 2000);
    } finally {
      setIsSyncing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FEFEFE] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#07E5D1]" />
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="min-h-screen bg-[#FEFEFE] flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-800 mb-2">Session Expired</h1>
          <p className="text-gray-600">
            {error || 'This upload session has expired. Please scan the QR code again from your computer.'}
          </p>
        </div>
      </div>
    );
  }

  if (isDone) {
    return (
      <div className="min-h-screen bg-[#FEFEFE] flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <CheckCircle className="w-16 h-16 text-[#07E5D1] mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-gray-800 mb-2">Photos Synced!</h1>
          <p className="text-gray-600 mb-2">
            {images.length} photo{images.length !== 1 ? 's have' : ' has'} been synced to your computer.
          </p>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-emerald-700">
              Your desktop has received all photos.
            </p>
          </div>
          <p className="text-sm text-gray-500">
            You can close this page and return to your computer to continue the survey.
          </p>
        </div>
      </div>
    );
  }

  // Only disable Done during active upload - extraction happens in background
  const isProcessing = uploadingKeys.size > 0 || isSyncing;

  return (
    <div className="min-h-screen bg-[#FEFEFE]">
      {/* Syncing overlay */}
      {isSyncing && (
        <div className="fixed inset-0 bg-white/90 flex items-center justify-center z-50">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-[#07E5D1] mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Syncing with Desktop</h2>
            <p className="text-gray-600">
              Confirming {images.length} photo{images.length !== 1 ? 's' : ''} uploaded...
            </p>
          </div>
        </div>
      )}

      <header className="bg-white border-b border-gray-100 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold text-gray-800">Photo Upload</h1>
          {images.length > 0 && (
            <button
              onClick={handleDone}
              disabled={isProcessing}
              className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors
                         ${isProcessing
                           ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                           : 'bg-[#07E5D1] text-white hover:bg-[#06D4C2]'}`}
            >
              {isSyncing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Syncing...
                </>
              ) : uploadingKeys.size > 0 ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Done
                </>
              )}
            </button>
          )}
        </div>
      </header>

      <main className="p-4 space-y-4">
        <input
          id="mobile-camera-input"
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleCapture}
          className="hidden"
        />

        {images.length === 0 ? (
          <button
            type="button"
            onClick={() => session && fileInputRef.current?.click()}
            disabled={!session}
            className={`w-full py-12 border-2 border-dashed rounded-xl flex flex-col
                       items-center justify-center gap-4 transition-colors
                       ${session
                         ? 'border-[#07E5D1] bg-[#E8FCFA] hover:bg-[#d0f9f5] cursor-pointer'
                         : 'border-gray-300 bg-gray-100 cursor-wait'}`}
          >
            {session ? (
              <>
                <Camera className="w-12 h-12 text-[#07E5D1]" />
                <span className="text-xl font-medium text-[#06D4C2]">Take Photo</span>
                <span className="text-sm text-[#07E5D1]">Tap to capture your pantry or fridge</span>
              </>
            ) : (
              <>
                <Loader2 className="w-12 h-12 text-gray-400 animate-spin" />
                <span className="text-xl font-medium text-gray-500">Loading session...</span>
              </>
            )}
          </button>
        ) : (
          <div className="space-y-4">
            {images.map((image) => (
              <div key={image.s3_key} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <div className="aspect-video relative bg-gray-100">
                  <img
                    src={image.localPreview || `${API_URL}${image.url}`}
                    alt="Uploaded photo"
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() => handleRemoveImage(image.s3_key)}
                    disabled={uploadingKeys.has(image.s3_key)}
                    className="absolute top-3 right-3 p-2 bg-red-500 text-white rounded-full
                               hover:bg-red-600 transition-colors shadow-md disabled:opacity-50"
                  >
                    <X className="w-5 h-5" />
                  </button>

                  {/* Per-image uploading overlay */}
                  {uploadingKeys.has(image.s3_key) && (
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <div className="bg-white rounded-lg px-4 py-2 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-[#07E5D1]" />
                        <span className="text-sm font-medium">Uploading...</span>
                      </div>
                    </div>
                  )}
                </div>

                {image.extraction_status === 'completed' && image.extracted_products?.length > 0 && (
                  <ProductExtractionResults
                    products={image.extracted_products}
                    showImage={false}
                  />
                )}

                {/* Show AI message only when there's a rejection reason AND no products found */}
                {image.rejection_reason && (!image.extracted_products || image.extracted_products.length === 0) && (
                  <div className="p-3 bg-amber-50 border-t border-amber-200">
                    <p className="text-sm font-medium text-amber-800 mb-1">
                      {image.rejection_reason ? 'AI Analysis:' : 'No products detected'}
                    </p>
                    {image.rejection_reason && (
                      <p className="text-sm text-amber-700">{image.rejection_reason}</p>
                    )}
                    {!image.rejection_reason && (
                      <p className="text-xs text-amber-600">
                        We couldn't identify products in this image.
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}

            <button
              type="button"
              onClick={() => images.length < 3 && fileInputRef.current?.click()}
              disabled={images.length >= 3}
              className={`w-full py-4 border-2 border-dashed rounded-xl transition-colors
                         flex items-center justify-center gap-2
                         ${images.length >= 3
                           ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-50'
                           : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-[#07E5D1] cursor-pointer'}`}
            >
              <Camera className="w-5 h-5 text-gray-500" />
              <span className="text-gray-600 font-medium">Add Another Photo</span>
              {uploadingKeys.size > 0 && (
                <span className="text-xs text-[#07E5D1]">({uploadingKeys.size} uploading)</span>
              )}
            </button>
          </div>
        )}

        {error && (
          <p className="text-sm text-red-600 text-center bg-red-50 p-3 rounded-lg">{error}</p>
        )}

        <p className="text-xs text-gray-500 text-center">
          Photos are securely uploaded and analyzed for products
        </p>
      </main>

      <PhotoValidationModal
        isOpen={validationModal.isOpen}
        error={validationModal.error}
        onRetake={handleRetake}
        onContinueAnyway={handleContinueAnyway}
      />
    </div>
  );
};

export default MobilePhotoUpload;
